import { Card } from "../../components/Card";
import { NotificationItem } from "./NotificationItem";
import { ReactComponent as Close } from "../../assets/icons/x.svg";
import { ReactComponent as Setting } from "../../assets/icons/setting.svg";
import { api } from "../../services/api";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NotificationSettings } from "./NotificationSettings";
import { LottieLoading } from "../../components/Loading/LottieLoading";
import { useUser } from "../../services/user-context";
import { useApp } from "../../services/app-context";
import { DEFAULT_NOTIFICATION_CATEGORIES } from "../../constant";

function CategoryItem({ category, onClick, isSelected }: { category: string, onClick: () => void, isSelected: boolean }) {
    return (
        <button onClick={onClick} className={`flex !h-8 items-center justify-center rounded-md px-3 ${isSelected ? "bg-primary3 text-white" : "bg-[#F4F4F5] hover:bg-gray2 text-black"}`}>
            <span className="text-[15px] font-normal">{category}</span>
        </button>
    )
}

export function Notification({ onClose }: { onClose: () => void }) {
    const { tenantProfile } = useApp();
    const categories = tenantProfile?.notificationCategories || DEFAULT_NOTIFICATION_CATEGORIES;
    const {fetchPendingNotifications} = useUser() as any;
    const [openSetting, setOpenSetting] = useState(false);
    const [filter, setFilter] = useState({
        category: "all",
        cursor: "",
        hasMore: true
    });
    const [notificationData, setNotificationData] = useState<any[]>([]);

    const { data, isLoading, refetch, isFetching } = useQuery({
        queryKey: ["notifications", filter.category, filter.cursor],
        queryFn: () => api.getNotifications(filter.cursor, filter.category),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
    });

    const loading = isFetching || isLoading;

    const loadMore = () => {
        if (filter.hasMore) {
            const links = data?.data?.links;
            if (links?.next) {
                const next = links.next.split("cursor=")?.[1];
                if (next) {
                    setFilter(prev => ({
                        ...prev,
                        cursor: next
                    }));
                }
            }
        }
    }

    useEffect(() => {
        if (loading) return;
        const links = data?.data?.links;
        if (links?.next) {
            const next = links.next.split("cursor=")?.[1];
            if (next) {
                setFilter(prev => ({
                    ...prev,
                    hasMore: true
                }));
            } else {
                setFilter(prev => ({
                    ...prev,
                    hasMore: false
                }));
            }
        } else {
            setFilter(prev => ({
                ...prev,
                hasMore: false
            }));
        }
        const newItems = data?.data?.data || [];
        if (filter.cursor === "") {
            setNotificationData(newItems);
        } else {
            setNotificationData(prev => [...prev, ...newItems]);
        }
    }, [data, loading]);

    useEffect(() => {
        // check if there is any notification that is not read
        const unread = notificationData.filter((item: any) => !item.seen);
        if (unread.length > 0) {
            // call api to mark as read
            api.seeNotifications().then((res) => {
                fetchPendingNotifications();
            });
        }
    }, [notificationData]);

    return (
        <Card className="fixed top-10 right-4  md:w-[540px] h-[calc(100vh-100px)] !px-8 !py-7 z-[5]">
            <div className="flex flex-col gap-2 h-full">
                <div className="flex items-center justify-between">
                    <button onClick={() => setOpenSetting(!openSetting)} className="flex items-center justify-center bg-[#F4F4F5] w-[34px] h-[34px] rounded-full"><Setting className="w-6 h-6" /></button>
                    <span className="text-base font-medium">{openSetting ? "Notifications Settings" : "Notifications"}</span>
                    <button onClick={onClose} className="flex items-center justify-center"><Close className="w-6 h-6" /></button>
                </div>
                {openSetting ? <NotificationSettings /> :
                    <>
                        <div className="flex items-center gap-2 my-4 flex-wrap">
                            {categories.map((category) => (
                                <CategoryItem
                                    key={category.value}
                                    category={category.name}
                                    onClick={() => {
                                        setFilter(prev => ({
                                            ...prev,
                                            category: category.value,
                                            cursor: "",
                                            hasMore: true
                                        }));
                                        setNotificationData([]);
                                    }}
                                    isSelected={category.value === filter.category}
                                />
                            ))}
                        </div>
                        <div className="flex flex-col px-2 gap-2 h-[calc(100%-130px)] overflow-y-auto">
                            {notificationData.map((item: any) => (
                                <NotificationItem key={item.id} notification={item} onClose={onClose} />
                            ))}
                            {
                                loading && (
                                    <LottieLoading className="!h-[unset]" />
                                )
                            }
                            {
                                notificationData.length === 0 && !loading && (
                                    <div className="flex items-center justify-center h-full">
                                        <span className="text-black/60 text-[15px] font-normal">You're all caught up!</span>
                                    </div>
                                )
                            }
                        </div>
                        {
                            filter.hasMore && (
                                <button onClick={loadMore} className="text-PraxisDarkRed text-[15px] font-normal">Load more</button>
                            )
                        }
                    </>}
            </div>
        </Card>
    )
}
